/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/lib/theme';
import { getLogo } from '@/config/project.config';
import projectConfig from '@/config/project.config';

export interface LegalSection {
  id: string;
  labelFr: string;
  labelEn: string;
}

interface LegalLayoutProps {
  title: { fr: string; en: string };
  lastUpdated: string;
  sections: LegalSection[];
  activeSection: string;
  lang: 'fr' | 'en';
  onLangToggle: () => void;
  children: React.ReactNode;
}

export function LegalLayout({
  title, lastUpdated, sections, activeSection, lang, onLangToggle, children,
}: LegalLayoutProps) {
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    document.title = `${lang === 'fr' ? title.fr : title.en} — ${projectConfig.name}`;
    return () => { document.title = projectConfig.name; };
  }, [lang, title]);

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <Link to="/auth/login" className="legal-logo-link">
          <img src={getLogo(isDark)} alt={projectConfig.name} className="legal-logo" />
          <span className="legal-platform-name">{projectConfig.name}</span>
        </Link>
        <div className="legal-header-actions">
          <button className="legal-theme-toggle" onClick={toggleTheme} type="button" title={isDark ? 'Light mode' : 'Dark mode'}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="legal-body">
        {/* Sticky TOC — desktop */}
        <nav className="legal-toc" role="navigation" aria-label={lang === 'fr' ? 'Table des matières' : 'Table of contents'}>
          <div className="legal-toc-title">
            {lang === 'fr' ? 'Table des matières' : 'Table of contents'}
          </div>
          <ol className="legal-toc-list">
            {sections.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`legal-toc-item ${activeSection === s.id ? 'active' : ''}`}
                  aria-current={activeSection === s.id ? 'true' : undefined}
                >
                  <span className="legal-toc-num">{i + 1}.</span>
                  {lang === 'fr' ? s.labelFr : s.labelEn}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Content */}
        <main className="legal-content">
          {/* Document header */}
          <div className="legal-doc-header">
            <div>
              <h1 className="legal-doc-title">{lang === 'fr' ? title.fr : title.en}</h1>
              <p className="legal-doc-date">
                {lang === 'fr' ? 'Dernière mise à jour' : 'Last updated'}: {lastUpdated}
              </p>
            </div>
            <button className="legal-lang-toggle" onClick={onLangToggle} type="button">
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>

          {/* Mobile TOC pills */}
          <div className="legal-pills" role="navigation" aria-label={lang === 'fr' ? 'Table des matières' : 'Table of contents'}>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`legal-pill ${activeSection === s.id ? 'active' : ''}`}
                aria-current={activeSection === s.id ? 'true' : undefined}
              >
                {lang === 'fr' ? s.labelFr : s.labelEn}
              </a>
            ))}
          </div>

          {/* Sections */}
          {children}
        </main>
      </div>

      {/* Footer */}
      <footer className="legal-footer">
        <span>© {new Date().getFullYear()} {projectConfig.legal.company}. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</span>
        <Link to="/auth/login" className="legal-footer-link">
          {lang === 'fr' ? '← Retour à l\'application' : '← Back to application'}
        </Link>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
        }
        .legal-header {
          border-bottom: 1px solid var(--color-border-subtle);
          padding: 16px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .legal-header-actions {
          display: flex; align-items: center; gap: 8px;
        }
        .legal-theme-toggle {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-md);
          padding: 6px 10px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .legal-theme-toggle:hover {
          border-color: var(--color-brand-primary);
          background: var(--color-bg-tertiary);
        }
        .legal-logo-link {
          display: flex; align-items: center; gap: 10px; text-decoration: none;
        }
        .legal-logo { height: 32px; width: auto; }
        .legal-platform-name {
          font-family: var(--font-heading);
          font-size: var(--text-lg);
          font-weight: 700;
          color: var(--color-text-primary);
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        .legal-body {
          display: flex;
          flex: 1;
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
          padding: 40px 32px;
          gap: 40px;
        }
        .legal-toc {
          width: 220px;
          flex-shrink: 0;
          position: sticky;
          top: 40px;
          align-self: flex-start;
          max-height: calc(100vh - 120px);
          overflow-y: auto;
        }
        .legal-toc-title {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--color-text-muted);
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .legal-toc-list { list-style: none; margin: 0; padding: 0; }
        .legal-toc-item {
          display: flex;
          gap: 6px;
          align-items: flex-start;
          padding: 6px 8px;
          border-radius: var(--radius-sm);
          font-size: 12px;
          color: var(--color-text-secondary);
          text-decoration: none;
          line-height: 1.4;
          margin-bottom: 2px;
          transition: all 0.15s ease;
          border-left: 2px solid transparent;
        }
        .legal-toc-item:hover { color: var(--color-text-primary); background: var(--color-bg-secondary); }
        .legal-toc-item.active {
          color: var(--color-brand-primary);
          border-left-color: var(--color-brand-primary);
          background: var(--color-bg-secondary);
          font-weight: 500;
        }
        .legal-toc-num { color: var(--color-text-muted); min-width: 18px; font-size: 11px; }
        .legal-content { flex: 1; min-width: 0; }
        .legal-doc-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .legal-doc-title {
          font-family: var(--font-heading);
          font-size: var(--text-2xl);
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 6px;
        }
        .legal-doc-date { font-size: var(--text-sm); color: var(--color-text-muted); margin: 0; }
        .legal-lang-toggle {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-md);
          padding: 6px 14px;
          font-size: var(--text-sm);
          font-weight: 600;
          color: var(--color-text-secondary);
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }
        .legal-lang-toggle:hover { color: var(--color-brand-primary); border-color: var(--color-brand-primary); }
        .legal-pills {
          display: none;
          gap: 6px;
          overflow-x: auto;
          position: sticky;
          top: 0;
          background: var(--color-bg-primary);
          padding: 10px 0;
          margin-bottom: 20px;
          border-bottom: 1px solid var(--color-border-subtle);
          scrollbar-width: none;
          -ms-overflow-style: none;
          z-index: 10;
        }
        .legal-pills::-webkit-scrollbar { display: none; }
        .legal-pill {
          flex-shrink: 0;
          padding: 4px 12px;
          border-radius: var(--radius-full);
          font-size: 11px;
          font-weight: 500;
          color: var(--color-text-secondary);
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-subtle);
          text-decoration: none;
          white-space: nowrap;
          transition: all 0.15s ease;
        }
        .legal-pill.active { background: var(--color-brand-primary); color: #fff; border-color: var(--color-brand-primary); }
        .legal-footer {
          border-top: 1px solid var(--color-border-subtle);
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
        }
        .legal-footer-link { color: var(--color-brand-primary); text-decoration: none; font-size: var(--text-sm); }
        .legal-footer-link:hover { text-decoration: underline; }
        @media (max-width: 768px) {
          .legal-toc { display: none; }
          .legal-pills { display: flex; }
          .legal-body { padding: 20px 16px; gap: 0; }
          .legal-header { padding: 12px 16px; }
          .legal-footer { flex-direction: column; gap: 8px; text-align: center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .legal-toc-item, .legal-lang-toggle, .legal-pill, .legal-theme-toggle { transition: none; }
        }
      `}</style>
    </div>
  );
}
